import {type ICartCalculationResponse} from '../../../@type/woocommerce/cart-calculation';
import {consumeCartCalculationResponse} from '../payment/order';
import {Feature, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {store} from '../store';
import {eventClick} from '../util/dom';
import {$qsAll} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {captureSentryException} from '../../../@shared/ts/sentry';
import {maybeFetch} from '../../../@shared/ts/maybe';
import {getErrorString} from '../../../@shared/ts/error';

export function initQuantityChangerEvent() {
	if (!Feature.enabled(FeatureFlag.QUANTITY_CHANGER)) {
		$qsAll('#pp-summary-body, #pp-summary-body-mobile', $removeButtons => {
			$removeButtons.addEventListener('click', async (event: Event) => {
				const $target = event.target as HTMLElement;

				if ($target.closest('.pp-item-remover-btn')) {
					const cartItemKey = $target.closest<HTMLElement>('.pp-item-remover-btn')?.dataset['qid'];
					await changeQuantity(cartItemKey, 0, true);
				}
			});
		});
		return;
	}

	// Targeting root of each cart list to simplify event listener and only ever set the listeners once.
	$qsAll('#pp-summary-body, #pp-summary-body-mobile, .pp-related-product-body', $cartContainer => {
		$cartContainer.addEventListener('click', handleCartContainerEvent);
		$cartContainer.addEventListener('keypress', handleCartContainerEvent);
	});
}

async function handleCartContainerEvent(event: MouseEvent | KeyboardEvent) {
	const $target = event.target as HTMLElement;
	// Item qty updates on Enter, Space, blur, or 3/4 second after being updated.
	if ($target.closest('.qty-fs') && event.type === 'keypress') { // Ignore clicks, we need those to select qty-fs.
		const $inputTarget = event.target as HTMLInputElement;
		const cartItemKey = $target.closest<HTMLElement>('.qty-fs')?.dataset['qid'];
		if ($inputTarget.value && $inputTarget.checkValidity()) {
			if (eventClick(event)) { // Update value immediately on Enter or Space.
				await changeQuantity(cartItemKey, Number.parseInt($inputTarget.value), true);
			} else if (event.type === 'keypress') { // Else if the qty was updated, set a blur event listener and the timer.
				$target.addEventListener('blur', async () => {
					await changeQuantity(cartItemKey, Number.parseInt($inputTarget.value), true);
				}, {once: true});
				await new Promise(r => {
					setTimeout(r, 750);
				});
				if (!document.activeElement!.classList.contains('qty-fs')) {
					// Do nothing if we've already blurred.
					return;
				}

				$target.blur();
			}
		}

		return;
	}

	// The rest of the events are eventClick events.
	if (!eventClick(event)) {
		return;
	}

	if (!$target.closest('.qty-btn') && !$target.closest('.qty-fs') && !$target.closest('.pp-item-remover-btn')) {
		return; // What was clicked was not a quantity button.
	}

	if ($target.closest('.qty-btn')) {
		const $button = $target.closest<HTMLElement>('.qty-btn');
		const cartItemKey = $button?.dataset['qid'];

		if ($button?.classList.contains('decrease-qty')) {
			await changeQuantity(cartItemKey, -1, false);
		} else if ($button?.classList.contains('increase-qty')) {
			await changeQuantity(cartItemKey, 1, false);
		}
	} else if ($target.closest('.pp-item-remover-btn')) {
		const cartItemKey = $target.closest<HTMLElement>('.pp-item-remover-btn')!.dataset['qid'];
		await changeQuantity(cartItemKey, 0, true);
	}
}

async function changeQuantity(cartItemKey: string | undefined, amount = 1, set = false) {
	const quantityChangeURL = Feature.metadata<string>(FeatureFlag.QUANTITY_CHANGER, 'quantity_changed_url');
	if (!quantityChangeURL || !cartItemKey) {
		return;
	}

	store.dispatch(startModalLoading());

	const formData = new FormData();
	formData.append('cart_item_key', cartItemKey);
	formData.append('quantity', String(amount));
	formData.append('absolute', String(set));

	const {error, result} = await maybeFetch<ICartCalculationResponse>(quantityChangeURL, {
		method: 'POST',
		body: formData,
	});

	if (error || !result) {
		captureSentryException(error instanceof Error ? error : new Error(getErrorString(error)));
		store.dispatch(stopModalLoading());

		return;
	}

	consumeCartCalculationResponse(result);

	store.dispatch(stopModalLoading());
}
